import {
  buildUseAggregateComputedRecord,
  buildUseComputedRecord,
  CustomAggregateRecord,
  CustomRecordForAggregates,
  WithServiceName,
} from 'resources/accounting/transaction/computedGetters'
import { KampaayDataProvider } from 'services/api/dataProvider'
import { NAMED_SERVICES } from 'services/api/entities/accounting/transaction'
import {
  Transaction,
  TransactionService,
} from 'services/api/entities/accounting/transaction/types/internal/transaction-models'
import { getCountry } from 'services/geo-management'
import { translate } from 'services/i18n'
import { keys, mapValues, nonMemoizer } from 'services/utils'
import { EntityDescriptor } from '.'

const countryCode = getCountry().code
const getComputedRecord = buildUseComputedRecord(nonMemoizer, countryCode)
const getComputedAggregateRecord = buildUseAggregateComputedRecord(
  nonMemoizer,
  countryCode
)

const getLabel = (servName: string) =>
  NAMED_SERVICES.some((s) => s === servName)
    ? translate(`form.headers.${servName}`)
    : servName

const getLabeledList = (obj: WithServiceName) => {
  return keys(obj).reduce((acc, entry) => {
    return entry === 'serviceName'
      ? {
          ...acc,
        }
      : {
          ...acc,
          [`${getLabel(obj.serviceName)} ${entry}`]: obj[entry],
        }
  }, {})
}

const getServicesLabeledList = (servicesArr: CustomRecordForAggregates[]) =>
  servicesArr.reduce(
    (acc, { extraCost, extraPrice, ...objRest }) => ({
      ...acc,
      ...getLabeledList(objRest),
      [objRest.serviceName + ' Extra Price']: extraPrice,
      [objRest.serviceName + ' Extra Cost']: extraCost,
    }),
    {}
  )

const getComputed = (serv: TransactionService) =>
  serv.experienceNames
    ? {
        ...getComputedRecord(serv),
        experienceNames: serv.experienceNames.join(' - '),
      }
    : getComputedRecord(serv)

export const transaction: EntityDescriptor<Transaction> = {
  format: (_dataProvider?: KampaayDataProvider) => (record) => {
    const {
      services,
      id,
      customerName,
      invoiceExpiryDate,
      discountPerc,
      customerFiscalCode,
      customizations,
      ...rest
    } = record

    const accountedServicesComputed: (CustomRecordForAggregates & {
      experienceNames?: string
    })[] = services.map(getComputed)

    const generalServicesComputed: CustomAggregateRecord & {
      serviceName: 'General'
    } = {
      ...getComputedAggregateRecord(
        accountedServicesComputed,
        customizations,
        discountPerc
      ),
      serviceName: 'General',
    }
    const mapped = {
      ...rest,
      customerName,
      customerFiscalCode,
      invoiceExpiryDate,
      ...getLabeledList(generalServicesComputed),
      ...getServicesLabeledList(accountedServicesComputed),
    }

    return mapValues(
      (v: unknown) => (typeof v === 'number' ? v.toFixed(2) : v),
      mapped
    )
  },
}
